import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Notification, Order } from "../../../services";
import CreateOrderPopup from "../../../components/orders-new/create-order-popup/CreateOrderPopup";
import OrdersContent from "../../../components/orders/content";
import SectionItem from "../../../components/orders-new/section-item";
import OrdersSectionItemTable from "../../../components/orders-new/section-item/components/OrdersSectionItemTable";
import DefaultPaginationNew from "../../../components/shared/DefaultPaginationNew";
import Loading from "../../../components/shared/Loading/Loading";
import OrderSectionItemTableMobile from "../../../components/orders-new/section-item/components/OrderSectionItemTableMobile";
import OrdersTab from "../../../components/orders-new/tabs/OrdersTab";
import { CLIENT_TABS } from "../../../mock";
import HeaderContentNew from "../../../components/shared/HeaderContentNew/HeaderContentNew";
import OrderFilterClient from "../../../components/orders-new/filter/OrderFilterClient";
import BulkSelectionBar from "../../../components/shared/BulkSelectionBar";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useOrderRealtimeEvents } from "../../../hooks/useOrderRealtimeEvents";
import { ORDER_LIST_REFRESH_TARGETS } from "../../../services/orderRealtimeEvents";
import { useScreenSize } from "../../../hooks/useScreenSize";
import { useLanguage } from "../../../context/LanguageContext";
import { useOrderFilter } from "../../../hooks/useOrderFilter";
import OrderInfoClient from "../../../components/orders-new/order-info-client/OrderInfoClient";
import { TRANSPORTATION_TYPES } from "../utils/railwayOrder";
import {
  createOrderListSnapshot,
  ORDER_LIST_STATE_KEY,
  readOrderListSnapshot,
} from "../utils/orderListNavigationState";

const ListsPage = ({ transportationType = TRANSPORTATION_TYPES.ROAD }) => {
  const isRailway = transportationType === TRANSPORTATION_TYPES.RAILWAY;
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const { user } = useSelector((state) => state);
  const navigate = useNavigate();
  const location = useLocation();
  const { isTabletSmallView } = useScreenSize();

  const initialSnapshotRef = useRef(readOrderListSnapshot(location.state));
  const initialSnapshot = initialSnapshotRef.current;

  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [statusActive, setStatus] = useState(
    () => CLIENT_TABS(t).find((tab) => tab.key === initialSnapshot?.tabKey) || null,
  );
  const [sorting, setSorting] = useState(() => initialSnapshot?.sorting || null);
  const [isLoading, setLoading] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [currentPage, changePage] = useState(() => initialSnapshot?.page || 1);
  const [selectedLimit, changeLimit] = useState(() => initialSnapshot?.limit || 10);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [notificationsMap, setNotificationsMap] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [unselectedIds, setUnselectedIds] = useState([]);
  const [selectionMode, setSelectionMode] = useState("partial");
  const [selectedOrdersById, setSelectedOrdersById] = useState({});
  const [activeOrderId, setActiveOrderId] = useState(null);
  const [pendingReopenOrderId, setPendingReopenOrderId] = useState(
    () => initialSnapshot?.reopenOrderId ?? null,
  );

  const isExpeditor = user?.type === "Expeditor";
  const [isCallTaps, setCallTaps] = useState(0);
  const shouldShowWarning = isExpeditor && location.pathname === "/main";
  const ordersRequestIdRef = useRef(0);
  const skipInitialPageResetRef = useRef(Boolean(initialSnapshot));
  const baseLocationStateRef = useRef(location.state || {});

  const {
    filterValues,
    filterSetters,
    filterMeta,
    filterDeps,
    isFilterOpen,
    setFilterOpen,
    resetFilter,
    getFilterParams,
  } = useOrderFilter(currentLanguage, initialSnapshot?.filters);

  const handleSetStatus = (item) => {
    setStatus(item);
  };

  const resetSelection = useCallback(() => {
    setSelectionMode("partial");
    setSelectedIds([]);
    setUnselectedIds([]);
    setSelectedOrdersById({});
  }, []);

  const handleChangeSortColumn = useCallback(
    (nextSorting) => {
      setSorting(nextSorting);
      if (currentPage !== 1) {
        changePage(1);
      }
    },
    [currentPage],
  );

  const [isCreatePopupOpen, setIsCreatePopupOpen] = useState(false);

  useEffect(() => {
    if (user && shouldShowWarning) {
      navigate('/orders/available', { replace: true });
    }
  }, [isExpeditor, location.pathname, navigate, user]);

  const getOrdersRequestParams = (overrides = {}) => ({
    ...getFilterParams({
      page: currentPage,
      limit: selectedLimit,
      statusActive,
      sorting,
      selectedIds,
      unselectedIds,
      ...overrides,
    }),
    transportationType,
  });

  const fetchData = async ({ silent = false } = {}) => {
    const requestId = ++ordersRequestIdRef.current;
    if (!silent) {
      setLoading(true);
    }

    try {
      const params = getOrdersRequestParams({ selectedIds: [], unselectedIds: [] });
      const data = await getOwnOrders(params);

      if (requestId !== ordersRequestIdRef.current) return;

      const rows = data.rows || [];
      setOrders(rows);
      setOrdersTotal(data?.total || 0);

      if (pendingReopenOrderId !== null) {
        const exists = rows.some(
          (order) => String(order.id) === String(pendingReopenOrderId),
        );
        setActiveOrderId(exists ? pendingReopenOrderId : null);
        setPendingReopenOrderId(null);
      }
    } catch (e) {
      if (requestId === ordersRequestIdRef.current) {
        console.error(e);
      }
    } finally {
      if (requestId === ordersRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const handleRefetchData = () => {
    setOrders([]);
    fetchData();
    setCallTaps((version) => version + 1);
  };

  const triggerForceUpdate = useCallback(() => {
    setForceUpdate((prev) => prev + 1);
  }, []);

  const handleEditOrder = useCallback(
    (editPath) => {
      const snapshot = createOrderListSnapshot({
        tabKey: statusActive?.key || null,
        page: currentPage,
        limit: selectedLimit,
        sorting,
        filters: filterValues,
        reopenOrderId: activeOrderId,
      });

      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: {
          ...baseLocationStateRef.current,
          [ORDER_LIST_STATE_KEY]: snapshot,
        },
      });
      navigate(editPath, { state: { returnToOrdersList: true } });
    },
    [
      activeOrderId,
      currentPage,
      filterValues,
      location.pathname,
      location.search,
      navigate,
      selectedLimit,
      sorting,
      statusActive?.key,
    ],
  );

  const handleOrderInfoVisibility = useCallback((nextIsOpen) => {
    if (!nextIsOpen) {
      setActiveOrderId(null);
    }
  }, []);

  useOrderRealtimeEvents(({ target }) => {
    if (!ORDER_LIST_REFRESH_TARGETS.has(target)) return;

    fetchData({ silent: true });
    setCallTaps((version) => version + 1);
  });

  useEffect(() => {
    if (!orders) return;

    const fetchNotifications = async () => {
      const updatedMap = {};

      for (const item of orders) {
        try {
          const response = await Notification.getOrderNotificationById(item.id);
          updatedMap[item.id] = response?.data || [];
        } catch (err) {
          console.error("Ошибка при получении уведомлений для заказа:", item.id, err);
          updatedMap[item.id] = [];
        }
      }

      setNotificationsMap(updatedMap);
    };

    if (orders.length > 0) {
      fetchNotifications();
    }
  }, [orders]);

  const getOwnOrders = async (params = {}) => Order.getOwnOrders(params).then((res) => res.data);

  const handleDownloadExcel = () => {
    setIsExportLoading(true);
    const params = getOrdersRequestParams({
      selectedIds: selectionMode === "all" ? [] : selectedIds,
      unselectedIds: selectionMode === "all" ? unselectedIds : [],
    });

    Order.getClientExportOrders(params)
      .then((response) => {
        if (response.status === 200) {
          const file = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const downloadElement = document.createElement("a");
          downloadElement.href = URL.createObjectURL(file);
          downloadElement.download = "client_export_orders.xlsx";
          document.body.appendChild(downloadElement);
          downloadElement.click();
          document.body.removeChild(downloadElement);
        } else {
          alert(t("ordersListPage.toast.downloadError"));
        }
      })
      .catch((error) => {
        console.error("Error downloading Excel:", error);
        alert(t("ordersListPage.toast.downloadError"));
      })
      .finally(() => {
        setIsExportLoading(false);
      });
  };

  const selectedCount = useMemo(() => {
    if (selectionMode === "all") {
      return Math.max(ordersTotal - unselectedIds.length, 0);
    }

    return selectedIds.length;
  }, [ordersTotal, selectedIds.length, selectionMode, unselectedIds.length]);

  useEffect(() => {
    if (selectionMode !== "partial") return;

    const currentOrdersById = new Map(orders.map((order) => [String(order.id), order]));
    setSelectedOrdersById((previousOrdersById) =>
      selectedIds.reduce((nextOrdersById, orderId) => {
        const key = String(orderId);
        const order = currentOrdersById.get(key) || previousOrdersById[key];
        if (order) nextOrdersById[key] = order;
        return nextOrdersById;
      }, {}),
    );
  }, [orders, selectedIds, selectionMode]);

  const selectedOrdersForCancellation = useMemo(
    () =>
      selectedIds
        .map((orderId) => selectedOrdersById[String(orderId)])
        .filter(Boolean),
    [selectedIds, selectedOrdersById],
  );

  const handleSelectAllOrders = () => {
    if (!ordersTotal) return;

    setSelectionMode("all");
    setSelectedIds([]);
    setUnselectedIds([]);
  };

  // Effect A: When filters, active tab, or limit change → reset page to 1 or fetch
  useEffect(() => {
    if (skipInitialPageResetRef.current) {
      skipInitialPageResetRef.current = false;
      resetSelection();
      return;
    }

    if (currentPage !== 1) {
      changePage(1);
    } else if (statusActive) {
      fetchData();
    }
    resetSelection();
  }, [...filterDeps, selectedLimit, currentLanguage, statusActive, transportationType]);

  // Effect B: When page, sort, or forceUpdate change → fetch
  useEffect(() => {
    if (!statusActive) return;
    fetchData();
  }, [currentPage, sorting, forceUpdate]);

  if (user && shouldShowWarning) {
    return (
      <div className="alert alert-warning text-center" role="alert">
        <strong>{t("common.warning")}</strong> {t("ordersCreate.errors.pageNotAvailableForTransporter")}
        <br />
        <br />
        <span>{t("ordersCreate.errors.redirectMessage")}</span>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <HeaderContentNew
        displayReloadBtn
        title={t(isRailway ? "railwayOrder.listTitle" : "ordersListPage.header.title")}
        displayCreateBtn
        onReload={handleRefetchData}
        onCreateBtnClick={() => {
          if (isRailway) navigate("/orders/railway/create");
          else setIsCreatePopupOpen((prev) => !prev);
        }}
        createBtnPopup={
          isCreatePopupOpen ? (
            <CreateOrderPopup onClose={() => setIsCreatePopupOpen(false)} />
          ) : null
        }
      />

      {/* Filter Panel */}
      <OrderFilterClient
        transportationType={transportationType}
        isOpen={isFilterOpen}
        onClose={() => setFilterOpen(false)}
        filterValues={filterValues}
        filterSetters={filterSetters}
        filterMeta={filterMeta}
        statusActive={statusActive}
        onSwitchToAll={() => {
          const allTab = CLIENT_TABS(t).find((tab) => tab.key === "all");
          if (allTab) setStatus(allTab);
        }}
      />

      {/* Tabs */}
      <OrdersTab
        transportationType={transportationType}
        setStatus={handleSetStatus}
        changeSortColumn={handleChangeSortColumn}
        sortColumn={sorting}
        onDownloadExcel={handleDownloadExcel}
        isExportLoading={isExportLoading}
        isCallTaps={isCallTaps}
        countryFrom={filterValues.countryFrom}
        countryTo={filterValues.countryTo}
        cityFrom={filterValues.cityFrom}
        cityTo={filterValues.cityTo}
        loadingDate={filterValues.loadingDate}
        creationDate={filterValues.creationDate}
        selectedStatus={filterValues.selectedStatus}
        selectedCurrency={filterValues.selectedCurrency}
        senderName={filterValues.senderName}
        receiverName={filterValues.receiverName}
        orderNo={filterValues.orderNo}
        executor={filterValues.executor}
        selectedEmployee={filterValues.selectedEmployee}
        insuranceIncluded={filterValues.insuranceIncluded}
        statusActive={statusActive}
        isFilterIcon={isTabletSmallView}
        onOpenFilter={() => setFilterOpen((prev) => !prev)}
        isFilterOpen={isFilterOpen}
        onReload={handleRefetchData}
        onReset={resetFilter}
      />

      {!statusActive && <Loading inBlock />}
      {/* All content */}
      {statusActive && (
        <>
          <OrdersContent>
            {/* First Section */}
            <SectionItem isTable={isTabletSmallView}>
              {isExportLoading ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px', minHeight: '200px' }}>
                  <Loading inBlock />
                  <p style={{ marginTop: '16px', fontSize: '16px', fontWeight: '500', textAlign: 'center' }}>
                    {t('ordersListPage.exportLoading')}
                  </p>
                </div>
              ) : (
                <>
                  <BulkSelectionBar
                    selectedCount={selectedCount}
                    totalCount={ordersTotal}
                    onSelectAll={handleSelectAllOrders}
                    onClear={resetSelection}
                    selectedOrders={selectedOrdersForCancellation}
                    isSelectAllMode={selectionMode === "all"}
                    onCancelSuccess={() => {
                      resetSelection();
                      handleRefetchData();
                    }}
                  />

                  <OrdersSectionItemTable
                    isRailway={isRailway}
                    items={orders || []}
                    isLoading={isLoading}
                    isExpeditor={isExpeditor}
                    companyName={user?.companyName}
                    notificationsMap={notificationsMap}
                    setNotificationsMap={setNotificationsMap}
                    selectedIds={selectedIds}
                    setSelectedIds={setSelectedIds}
                    unselectedIds={unselectedIds}
                    setUnselectedIds={setUnselectedIds}
                    selectionMode={selectionMode}
                    onOpenOrder={setActiveOrderId}
                  />

                  <OrderSectionItemTableMobile
                    isRailway={isRailway}
                    onOpenOrder={setActiveOrderId}
                    notificationsMap={notificationsMap}
                    setNotificationsMap={setNotificationsMap}
                    sortColumn={sorting}
                    items={orders || []}
                    isLoading={isLoading}
                    isExpeditor={isExpeditor}
                  />

                    {orders && orders.length ? (
                      <DefaultPaginationNew
                        total={ordersTotal}
                        currentPage={currentPage}
                        limit={selectedLimit}
                        changePage={changePage}
                        changeLimit={changeLimit}
                        isLoading={isLoading}
                      />
                    ) : null}
                  </>
                )}
            </SectionItem>
          </OrdersContent>

          <OrderInfoClient
            isOpen={activeOrderId !== null}
            setIsOpen={handleOrderInfoVisibility}
            orderId={activeOrderId}
            companyName={user?.companyName}
            onForceUpdate={triggerForceUpdate}
            onEditOrder={handleEditOrder}
          />
        </>
      )}
    </>
  );
};

export default ListsPage;
